// ============================================================================
// API CONFIGURATION
// ============================================================================
// Centralized configuration for all API-related settings

/**
 * API Configuration Interface
 */
export interface ApiConfig {
  baseURL: string;
  timeout: number;
  enableLogging: boolean;
  tokenKey: string;
  refreshTokenKey: string;
  apiVersion: string;
}

/**
 * Get API Configuration from Environment Variables
 */
export const apiConfig: ApiConfig = {
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:5006/api',
  timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || '30000', 10),
  enableLogging: process.env.REACT_APP_ENABLE_API_LOGGING === 'true',
  tokenKey: process.env.REACT_APP_TOKEN_KEY || 'auth_token',
  refreshTokenKey: process.env.REACT_APP_REFRESH_TOKEN_KEY || 'refresh_token',
  apiVersion: process.env.REACT_APP_API_VERSION || 'v1',
};

/**
 * API Response Status Codes
 */
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
} as const;

/**
 * API Error Messages
 */
export const API_ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your internet connection.',
  TIMEOUT_ERROR: 'Request timeout. Please try again.',
  UNAUTHORIZED: 'Unauthorized. Please login again.',
  FORBIDDEN: 'You do not have permission to perform this action.',
  NOT_FOUND: 'Resource not found.',
  SERVER_ERROR: 'Server error. Please try again later.',
  UNKNOWN_ERROR: 'An unexpected error occurred.',
} as const;

/**
 * Token Management Utilities
 */
export const tokenUtils = {
  /**
   * Get access token from localStorage
   */
  getToken(): string | null {
    return localStorage.getItem(apiConfig.tokenKey);
  },

  /**
   * Set access token in localStorage
   */
  setToken(token: string): void {
    localStorage.setItem(apiConfig.tokenKey, token);
  },

  /**
   * Remove access token from localStorage
   */
  removeToken(): void {
    localStorage.removeItem(apiConfig.tokenKey);
  },

  /**
   * Get refresh token from localStorage
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(apiConfig.refreshTokenKey);
  },

  /**
   * Set refresh token in localStorage
   */
  setRefreshToken(token: string): void {
    localStorage.setItem(apiConfig.refreshTokenKey, token);
  },

  /**
   * Remove refresh token from localStorage
   */
  removeRefreshToken(): void {
    localStorage.removeItem(apiConfig.refreshTokenKey);
  },

  /**
   * Clear all tokens
   */
  clearTokens(): void {
    this.removeToken();
    this.removeRefreshToken();
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getToken();
  },
};

/**
 * Export configuration and utilities
 */
export default apiConfig;
